import { ApiError } from './api';
import { isTestModeEnabled } from './testMode';
/**
 * The browser half of the two-step upload used by tasks and leads: ask the API
 * for a signed URL, then PUT the bytes straight at it.
 *
 * Both modules ran identical copies of this, which is how they ended up with
 * identical defects — no size check, and `Upload failed (413)` as the only
 * account a user ever got of a 1.3 MB PDF that nginx had rejected upstream.
 *
 * Transfers go through XMLHttpRequest rather than `fetch` for one reason:
 * `fetch` cannot report how many bytes have left the browser, so every upload
 * in the app was a spinner with no end in sight. XHR's `upload.onprogress`
 * gives us a real percentage, and `upload.onload` tells us the moment the last
 * byte is out — after which the wait is the server's (Drive write, PDF parse,
 * transcription), which the UI shows as an indeterminate "processing" phase.
 */

/**
 * Largest file the storage proxy can ever accept. Mirrors UPLOAD_HARD_CAP_BYTES
 * in backend/src/services/appConfig.ts and `client_max_body_size` in the nginx
 * configs — including the host vhost, which the deploy keeps in step.
 */
export const MAX_UPLOAD_BYTES = 2048 * 1024 * 1024;

// The limit an admin set in Settings → Google Drive. `useUploadLimit` (mounted
// once in the app layout) fills it in; until then the hard cap stands, and the
// storage proxy enforces the real limit either way.
let configuredLimitBytes = MAX_UPLOAD_BYTES;

export function setUploadLimitBytes(bytes: number): void {
  configuredLimitBytes = Math.min(bytes, MAX_UPLOAD_BYTES);
}

/** The single-file limit currently in force, in bytes. */
export function getUploadLimitBytes(): number {
  return configuredLimitBytes;
}

/** Whole megabytes, for messages: 26214400 → 25. */
export function bytesToMb(bytes: number): number {
  return Math.round(bytes / (1024 * 1024));
}

/** Whether a file fits under the single-file limit in force. */
export function isWithinUploadLimit(file: File): boolean {
  return file.size <= configuredLimitBytes;
}

export interface UploadedAttachment {
  url: string;
  name: string;
  contentType: string;
}

/**
 * Where an upload is in its life:
 *  - `uploading`  — bytes are moving; `percent` is real and grows.
 *  - `processing` — every byte is out, the server is still working (writing to
 *    Drive, parsing the PDF, transcribing the audio). No percentage exists for
 *    this, so the UI shows an indeterminate bar instead of a frozen 100%.
 */
export type UploadPhase = 'uploading' | 'processing';

export interface UploadProgress {
  /** Bytes transferred so far. */
  loaded: number;
  /** Total bytes to transfer; 0 when the browser cannot tell. */
  total: number;
  /** 0–100. Held at 99 while bytes are still moving, so 100 means "sent". */
  percent: number;
  phase: UploadPhase;
}

export type UploadProgressHandler = (progress: UploadProgress) => void;

interface SendOptions {
  method: 'PUT' | 'POST';
  headers?: Record<string, string>;
  onProgress?: UploadProgressHandler;
}

interface SendResult {
  status: number;
  /** Raw response body; callers parse it if they need to. */
  text: string;
}

/**
 * Send `body` to `url` and report transfer progress along the way.
 *
 * Resolves for every completed HTTP exchange, 4xx and 5xx included — status
 * handling belongs to the caller, which knows what a 413 means in its context.
 * Rejects only when the transfer itself fails (network down, request aborted).
 */
function sendWithProgress(url: string, body: Blob, options: SendOptions): Promise<SendResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method, url, true);
    for (const [name, value] of Object.entries(options.headers ?? {})) {
      xhr.setRequestHeader(name, value);
    }

    const total = body.size;
    const report = options.onProgress;

    if (report) {
      // Announce the starting state immediately: a large file can spend a
      // noticeable moment before the first progress event arrives, and an
      // upload row with no numbers in it looks stuck.
      report({ loaded: 0, total, percent: 0, phase: 'uploading' });

      xhr.upload.onprogress = (event) => {
        const loaded = event.loaded;
        const known = event.lengthComputable ? event.total : total;
        // Cap at 99: 100 is reserved for "the bytes are gone", which is a
        // different (and usually much longer) wait than the transfer itself.
        const percent = known > 0 ? Math.min(99, Math.round((loaded / known) * 100)) : 0;
        report({ loaded, total: known, percent, phase: 'uploading' });
      };

      xhr.upload.onload = () => {
        report({ loaded: total, total, percent: 100, phase: 'processing' });
      };
    }

    xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText });
    xhr.onerror = () => reject(new Error('Conexiunea s-a întrerupt în timpul încărcării.'));
    xhr.onabort = () => reject(new Error('Încărcarea a fost anulată.'));
    xhr.ontimeout = () => reject(new Error('Încărcarea a expirat.'));

    xhr.send(body);
  });
}

/** Reject anything above the proxy's cap before a byte leaves the browser. */
export function assertWithinUploadLimit(file: File): void {
  if (!isWithinUploadLimit(file)) {
    const mb = Math.ceil(file.size / (1024 * 1024));
    throw new Error(
      `„${file.name}” are ${mb} MB, peste limita de ${bytesToMb(configuredLimitBytes)} MB per fișier.`,
    );
  }
}

/**
 * PUT a file at a signed upload URL, reporting progress.
 *
 * This is the single transport behind every signed-URL upload in the app —
 * project, client, supplier and person resources, and meeting-note PDFs and
 * audio. Callers used to keep private `fetch` copies of it, which is why four
 * of them drifted apart.
 */
export async function putFileWithProgress(
  uploadUrl: string,
  file: File,
  contentType: string,
  onProgress?: UploadProgressHandler,
): Promise<void> {
  assertWithinUploadLimit(file);
  const { status, text } = await sendWithProgress(uploadUrl, file, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    onProgress,
  });
  if (status < 200 || status >= 300) {
    // A 413 from the API names the limit in its body; one from a proxy in front
    // of it is bare, so its cap is below ours — worth saying in both cases.
    let fromBody: string | undefined;
    try {
      fromBody = (JSON.parse(text) as { error?: { message?: string } }).error?.message;
    } catch {
      fromBody = undefined;
    }
    throw new Error(
      status === 413
        ? (fromBody ?? `„${file.name}” a fost respins de server ca fiind prea mare.`)
        : `Încărcarea „${file.name}” a eșuat (${status}).`,
    );
  }
}

/**
 * POST a file as the raw request body and return the parsed JSON envelope,
 * reporting progress. Used by uploads the server answers with a result rather
 * than an empty 200 — the bank-statement import, which parses the PDF before
 * it replies.
 */
export async function postFileWithProgress<T>(
  url: string,
  file: File,
  options: {
    headers?: Record<string, string>;
    onProgress?: UploadProgressHandler;
    /** Message for a non-2xx response when the body carries no error. */
    fallbackError?: (status: number) => string;
  } = {},
): Promise<T> {
  const { status, text } = await sendWithProgress(url, file, {
    method: 'POST',
    headers: options.headers,
    onProgress: options.onProgress,
  });

  let parsed: unknown;
  try {
    parsed = text ? (JSON.parse(text) as unknown) : undefined;
  } catch {
    parsed = undefined;
  }

  if (status < 200 || status >= 300) {
    const body = (parsed as { error?: { message?: string; code?: string } } | undefined)?.error;
    // An ApiError, so callers can tell a refused duplicate (409) from a failure.
    throw new ApiError(
      status,
      body?.message ??
        options.fallbackError?.(status) ??
        `Încărcarea „${file.name}” a eșuat (${status}).`,
      body?.code,
    );
  }
  return parsed as T;
}

/**
 * Upload `file` and return the attachment to embed in markdown or attach to a
 * comment. `mintUrl` is the module's own `POST /:id/upload-url` call.
 *
 * Errors are thrown in Romanian and named by cause, because callers render the
 * message verbatim.
 */
export async function uploadViaSignedUrl(
  file: File,
  mintUrl: (
    filename: string,
    contentType: string,
  ) => Promise<{ uploadUrl: string; publicUrl: string }>,
  onProgress?: UploadProgressHandler,
): Promise<UploadedAttachment> {
  assertWithinUploadLimit(file);

  const contentType = file.type || 'application/octet-stream';
  // Test Mode has no storage behind it: the mocked mint answers without an
  // uploadUrl, and the PUT failed ("Cannot load an empty url" in the app). The
  // file is attached by name only, which is all the demo shows.
  if (isTestModeEnabled()) return { url: '', name: file.name, contentType };
  const { uploadUrl, publicUrl } = await mintUrl(file.name, contentType);
  await putFileWithProgress(uploadUrl, file, contentType, onProgress);
  return { url: publicUrl, name: file.name, contentType };
}

/**
 * Read a file as base64, reporting how much of it has been read.
 *
 * Chat attachments are posted as JSON rather than raw bytes, so the browser
 * spends real time encoding before a single byte goes out. That read is a
 * measurable share of the wait; the POST that follows is not, and shows as the
 * `processing` phase. Returns the payload without the `data:<mime>;base64,`
 * prefix.
 */
export function readFileAsBase64(file: File, onProgress?: UploadProgressHandler): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const total = file.size;

    if (onProgress) {
      onProgress({ loaded: 0, total, percent: 0, phase: 'uploading' });
      reader.onprogress = (event) => {
        const known = event.lengthComputable ? event.total : total;
        const percent = known > 0 ? Math.min(99, Math.round((event.loaded / known) * 100)) : 0;
        onProgress({ loaded: event.loaded, total: known, percent, phase: 'uploading' });
      };
    }

    reader.onload = () => {
      onProgress?.({ loaded: total, total, percent: 100, phase: 'processing' });
      const result = String(reader.result ?? '');
      // Strip the "data:<mime>;base64," prefix.
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('Could not read the file'));
    reader.readAsDataURL(file);
  });
}
